import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
} from '@mlm/ui';
import {
  getTenantFeatures,
  listCompPlanConfigs,
  publishCompPlanConfig,
  type CompPlanConfigRow,
} from '../lib/api';
import { Link } from 'react-router-dom';
import { hasRole } from '../lib/auth-store';
import {
  CompPlanStructuredEditor,
  isPerTierPlan,
  planDescription,
  planDisplayName,
  planTrigger,
  SUPPORTED_PLAN_CODES,
  TRIGGER_LABEL,
  TRIGGER_ORDER,
} from '../components/compplan';
import { PerTierPlanEditor } from '../components/compplan/per-tier-editor';
import { diffConfig, RateDiffPreview } from '../components/diff-summary';

/**
 * Phase 12.5 ComPlan Configs editor. Replaces the prior raw-JSON textarea
 * with a per-plan structured form. The publish flow is unchanged — each
 * save inserts a NEW version row; existing versions stay immutable.
 *
 * Only super_admin can publish (per CLAUDE.md "Developer-only permissions").
 * Everyone else sees the editor in read-only mode and the version history.
 */
export function CompPlanPage() {
  const queryClient = useQueryClient();
  const canWrite = hasRole('super_admin');
  const query = useQuery({
    queryKey: ['admin-compplan-configs'],
    queryFn: listCompPlanConfigs,
  });
  const featuresQuery = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
  });
  const disabledPlans = useMemo(
    () => new Set(featuresQuery.data?.disabled_plans ?? []),
    [featuresQuery.data?.disabled_plans],
  );

  const byPlan = useMemo(() => {
    const map = new Map<string, CompPlanConfigRow[]>();
    for (const row of query.data ?? []) {
      const list = map.get(row.planCode) ?? [];
      list.push(row);
      map.set(row.planCode, list);
    }
    for (const list of map.values()) list.sort((a, b) => b.version - a.version);
    return map;
  }, [query.data]);

  // Stable plan list: only the plans the framework currently knows about
  // (`SUPPORTED_PLAN_CODES`). Retired plans whose `comp_plan_configs` rows
  // are still in the DB for audit (e.g. `referral`) are deliberately hidden
  // — the editor would expose values that the worker no longer reads.
  //
  // Per-tier plans don't have comp_plan_configs rows but still need to
  // appear here so the admin can configure them; their editor reads from
  // `memberships.config`.
  //
  // Disabled plans (`tenant_config.features.disabled_plans`) are also
  // hidden — admins who don't run them shouldn't be tweaking their configs.
  //
  // `sign_up_vortex` is retired from this editor: the Vortex plan's activation
  // tokens already cover the sign-up vortex entry, so a separate per-tier
  // sign-up vortex bonus is redundant + confusing. It stays in
  // SUPPORTED_PLAN_CODES so the Settings toggle can still disable its runtime.
  //
  // `mentor_bonus` ("Legacy Mentor bonus") is likewise hidden: despite the
  // name, its %-of-order-PV basis never existed in the old system — the
  // faithful port of the legacy mentor bonus is the Legacy Binary variant's
  // Mentor's Bonus rider (Binary editor → Enable Mentor's Bonus). Hiding it
  // keeps admins from configuring the wrong one; the plan itself stays
  // registered so any existing per-tier config keeps paying.
  const planList = useMemo(() => {
    return SUPPORTED_PLAN_CODES.filter(
      (code) =>
        code !== 'sign_up_vortex' && code !== 'mentor_bonus' && !disabledPlans.has(code),
    );
  }, [disabledPlans]);

  // The Binary plan's active (published) variant. Selects which per-tier binary
  // table to show: 'legacy' → daily cycle limits (income cap / max pairs / max
  // strong leg); 'teslab' → per-run distribution cap.
  const binaryVariantId =
    ((byPlan.get('binary')?.[0]?.config as { variant_id?: string } | undefined)?.variant_id) ??
    'legacy';

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // The binary variant currently SELECTED in the editor draft — which differs
  // from the PUBLISHED `binaryVariantId` until you Publish. The per-tier binary
  // section follows this so picking a variant in the dropdown swaps the per-tier
  // editor live (else you'd see the old variant's per-tier tables until save).
  const [editedBinaryVariant, setEditedBinaryVariant] = useState<string | undefined>(undefined);
  const effectiveBinaryVariant = editedBinaryVariant ?? binaryVariantId;
  useEffect(() => {
    // Auto-select first plan, or re-select if the current pick got disabled.
    if (planList.length === 0) {
      if (selectedPlan !== null) setSelectedPlan(null);
      return;
    }
    if (!selectedPlan || !(planList as readonly string[]).includes(selectedPlan)) {
      setSelectedPlan(planList[0]!);
    }
  }, [planList, selectedPlan]);

  const hiddenCount = disabledPlans.size;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Comp Plan configs</h1>
        <p className="text-sm text-muted-foreground">
          Each save publishes a NEW version row; existing versions stay
          immutable. Plans + workers read the latest version automatically.
          {!canWrite ? (
            <span className="text-destructive ml-2">
              Read-only. Only super_admin can publish.
            </span>
          ) : null}
        </p>
        {hiddenCount > 0 ? (
          <p className="text-xs text-muted-foreground mt-1">
            {hiddenCount} plan{hiddenCount === 1 ? '' : 's'} hidden because
            disabled in{' '}
            <Link to="/settings" className="underline">
              Tenant Settings
            </Link>
            .
          </p>
        ) : null}
      </div>

      {query.isLoading || featuresQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : planList.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              {hiddenCount > 0 ? (
                <>
                  All comp plans are disabled in{' '}
                  <Link to="/settings" className="underline">
                    Tenant Settings
                  </Link>
                  . Re-enable at least one to edit its configuration.
                </>
              ) : (
                <>
                  No comp plan configs yet. Run{' '}
                  <code className="text-xs font-mono">pnpm db:seed-compplans</code>{' '}
                  to seed the defaults.
                </>
              )}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-1 h-fit">
            <CardHeader>
              <CardTitle>Plans</CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-3">
              {TRIGGER_ORDER.map(
                (trigger) => {
                  const group = planList.filter(
                    (code) => planTrigger(code) === trigger,
                  );
                  if (group.length === 0) return null;
                  return (
                    <div key={trigger} className="space-y-1">
                      <p className="px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        {TRIGGER_LABEL[trigger]}
                      </p>
                      {group.map((plan) => {
                        const versions = byPlan.get(plan);
                        const isActive = selectedPlan === plan;
                        const perTier = isPerTierPlan(plan);
                        return (
                          <button
                            key={plan}
                            onClick={() => setSelectedPlan(plan)}
                            className={
                              'w-full text-left p-2 rounded-md text-sm transition-colors ' +
                              (isActive
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-muted text-foreground')
                            }
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">
                                {planDisplayName(plan)}
                              </span>
                              {perTier ? (
                                <Badge variant="muted">per-tier</Badge>
                              ) : versions ? (
                                <Badge variant="muted">v{versions[0]!.version}</Badge>
                              ) : (
                                <Badge variant="outline">no config</Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                              {plan}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  );
                },
              )}
            </CardContent>
          </Card>

          <div className="lg:col-span-3 space-y-4">
            {selectedPlan ? (
              isPerTierPlan(selectedPlan) ? (
                <>
                  {/* Monthly Direct Bonus is per-tier, but its mentor rider has
                      a plan-LEVEL master switch (stored in comp_plan_configs).
                      Render that toggle above the per-tier rates so the whole
                      mentor decision — on/off plus each tier's % — lives in one
                      place. */}
                  {selectedPlan === 'monthly_direct_bonus' ? (
                    <MonthlyDirectMentorToggle
                      versions={byPlan.get('monthly_direct_bonus') ?? []}
                      canWrite={canWrite}
                      onPublish={(config) =>
                        publishCompPlanConfig({
                          planCode: 'monthly_direct_bonus',
                          config,
                        }).then(() =>
                          queryClient.invalidateQueries({
                            queryKey: ['admin-compplan-configs'],
                          }),
                        )
                      }
                    />
                  ) : null}
                  <PerTierPlanEditor planCode={selectedPlan} canWrite={canWrite} />
                </>
              ) : (
                <>
                  <PlanEditor
                    planCode={selectedPlan}
                    versions={byPlan.get(selectedPlan) ?? []}
                    canWrite={canWrite}
                    onVariantChange={
                      selectedPlan === 'binary' ? setEditedBinaryVariant : undefined
                    }
                    onPublish={(config) =>
                      publishCompPlanConfig({ planCode: selectedPlan, config }).then(() =>
                        queryClient.invalidateQueries({
                          queryKey: ['admin-compplan-configs'],
                        }),
                      )
                    }
                  />
                  {/* Vortex is a flat plan, but its graduation rewards are per-tier —
                      edit them here (same component family as direct/indirect) so all
                      Vortex config lives in one place. */}
                  {selectedPlan === 'vortex' ? (
                    <PerTierPlanEditor
                      planCode="vortex"
                      canWrite={canWrite}
                      descriptionOverride="Per-tier Vortex graduation rewards: cash, GC, and token grants paid to a graduating slot of each tier."
                    />
                  ) : null}
                  {/* Australian pass-up is a flat plan (pass_up_start), but the
                      payout rates are per-tier (earner × buyer) — edit them here
                      so all of the plan's config lives in one place. */}
                  {selectedPlan === 'australian_pass_up' ? (
                    <PerTierPlanEditor
                      planCode="australian_pass_up"
                      canWrite={canWrite}
                      descriptionOverride="Per-tier pass-up rates: cash paid to the earning slot (the sponsor or the ancestor the income passes up to), looked up by the earner's tier × the new member's tier."
                    />
                  ) : null}
                  {/* Stairstep is a flat plan (ranks), but per-tier participation
                      (exclude a tier from ranking + distribution) is a membership
                      override — edit it here so all Stairstep config is together. */}
                  {selectedPlan === 'stairstep' ? (
                    <PerTierPlanEditor
                      planCode="stairstep"
                      canWrite={canWrite}
                      descriptionOverride="Per-tier Stairstep participation. Exclude a tier so its slots never rank or rank up and earn no Stairstep distribution; their volume still rolls up to uplines."
                    />
                  ) : null}
                  {/* Binary is a flat plan, but its daily limits are a per-tier
                      membership override — edit them here so all Binary config
                      lives in one place. The table shown matches the live
                      variant: Legacy → per-tier daily cycle limits (income cap /
                      max pairs / max strong leg); Teslab → per-run income cap. */}
                  {selectedPlan === 'binary' ? (
                    <PerTierPlanEditor
                      planCode="binary"
                      canWrite={canWrite}
                      binaryVariant={effectiveBinaryVariant}
                      descriptionOverride={
                        effectiveBinaryVariant === 'teslab'
                          ? "Per-tier Teslab per-distribution income cap: the most a tier's slot can earn in one Binary (Teslab) distribution run; the excess is flushed."
                          : effectiveBinaryVariant === 'extreme'
                            ? 'Per-tier Extreme Binary config: override the daily limits (income cap / max pairs / max strong leg) per membership tier, and give each RECEIVER tier its own pairing table + GC riders + total-flushout. Blank / empty = use the flat config above.'
                            : 'Per-tier Legacy Binary daily limits: override the daily income cap, max pairs/day, and max strong leg per membership tier so a higher tier can pair and earn more than a lower one. Blank / 0 = use the plan default above.'
                      }
                    />
                  ) : null}
                </>
              )
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Plan-level master switch for the Monthly Direct Bonus (G15) mentor rider.
 * Stored in `comp_plan_configs.monthly_direct_bonus.enable_mentor_rider` (a new
 * immutable version per change, same publish flow as the flat plans). The
 * per-tier Mentor % rates live just below in the per-tier editor; this is the
 * on/off that gates them all.
 */
function MonthlyDirectMentorToggle({
  versions,
  canWrite,
  onPublish,
}: {
  versions: CompPlanConfigRow[];
  canWrite: boolean;
  onPublish: (config: Record<string, unknown>) => Promise<unknown>;
}) {
  const latest = versions[0];
  const latestConfig = (latest?.config as Record<string, unknown> | undefined) ?? {};
  const savedEnabled = latestConfig.enable_mentor_rider === true;
  const [enabled, setEnabled] = useState(savedEnabled);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(savedEnabled);
    setSavedVersion(null);
    setError(null);
  }, [latest?.version, savedEnabled]);

  const dirty = enabled !== savedEnabled;
  const nextVersion = (latest?.version ?? 0) + 1;

  const publish = useMutation({
    meta: { successMessage: 'Mentor rider published.' },
    mutationFn: async () =>
      onPublish({ ...latestConfig, enable_mentor_rider: enabled }),
    onSuccess: () => {
      setSavedVersion(nextVersion);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'publish_failed');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Mentor rider
          <Badge variant="muted">master switch</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={enabled}
            disabled={!canWrite}
            onCheckedChange={(v) => setEnabled(v === true)}
            className="mt-1"
          />
          <div className="space-y-0.5">
            <div className="text-sm font-medium">Pay the sponsor's mentor</div>
            <p className="text-xs text-muted-foreground">
              When on, each qualifying sponsor's direct upline (mentor) also earns
              a percentage of that sponsor's monthly bonus. The rate is each tier's{' '}
              <strong>Mentor %</strong> in the per-tier table below; a tier with 0%
              pays no mentor. Off by default.
            </p>
          </div>
        </label>

        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        {savedVersion ? (
          <p className="text-xs text-success">Published v{savedVersion}.</p>
        ) : null}

        {canWrite ? (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Button
              onClick={() => {
                setError(null);
                publish.mutate();
              }}
              disabled={publish.isPending || !dirty}
            >
              {publish.isPending
                ? 'Saving…'
                : enabled
                  ? 'Enable mentor rider'
                  : 'Disable mentor rider'}
            </Button>
            <Button
              variant="outline"
              disabled={!dirty || publish.isPending}
              onClick={() => {
                setEnabled(savedEnabled);
                setError(null);
                setSavedVersion(null);
              }}
            >
              Reset
            </Button>
            {dirty ? (
              <span className="text-xs text-muted-foreground">Unsaved change</span>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PlanEditor({
  planCode,
  versions,
  canWrite,
  onPublish,
  onVariantChange,
}: {
  planCode: string;
  versions: CompPlanConfigRow[];
  canWrite: boolean;
  onPublish: (config: Record<string, unknown>) => Promise<unknown>;
  /** Reports the draft's currently-selected `variant_id` (binary uses it to
   *  keep the per-tier section in sync with the dropdown before Publish). */
  onVariantChange?: (variantId: string | undefined) => void;
}) {
  const latest = versions[0];
  const [draft, setDraft] = useState<Record<string, unknown>>(
    (latest?.config as Record<string, unknown> | undefined) ?? {},
  );
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setDraft((latest?.config as Record<string, unknown> | undefined) ?? {});
    setSavedVersion(null);
    setError(null);
  }, [planCode, latest?.version, latest?.config]);

  // Surface the draft's selected variant to the parent so the binary per-tier
  // section follows the dropdown live (before Publish). No-op for plans that
  // don't pass the callback.
  useEffect(() => {
    onVariantChange?.((draft as { variant_id?: string }).variant_id);
  }, [draft, onVariantChange]);

  const dirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(latest?.config ?? {});
  }, [draft, latest?.config]);

  const diffRows = useMemo(
    () => (confirming ? diffConfig(latest?.config ?? {}, draft) : []),
    [confirming, latest?.config, draft],
  );

  const nextVersion = (latest?.version ?? 0) + 1;

  const publish = useMutation({
    meta: { successMessage: 'Comp plan published.' },
    mutationFn: async () => onPublish(draft),
    onSuccess: () => {
      setSavedVersion(nextVersion);
      setError(null);
      setConfirming(false);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'publish_failed');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>
            {planDisplayName(planCode)}{' '}
            <span className="text-sm font-normal text-muted-foreground font-mono">
              (latest v{latest?.version ?? '?'})
            </span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">{planDescription(planCode)}</p>
        <CompPlanStructuredEditor
          planCode={planCode}
          value={draft}
          onChange={setDraft}
          readOnly={!canWrite}
        />

        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        {savedVersion ? (
          <p className="text-xs text-success">
            Published v{savedVersion}.
          </p>
        ) : null}

        {canWrite ? (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Button
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
              disabled={publish.isPending || !dirty}
            >
              {publish.isPending ? 'Publishing…' : 'Publish new version'}
            </Button>
            <Button
              variant="outline"
              disabled={!dirty}
              onClick={() => {
                setDraft((latest?.config as Record<string, unknown> | undefined) ?? {});
                setError(null);
                setSavedVersion(null);
              }}
            >
              Reset
            </Button>
            {dirty ? (
              <span className="text-xs text-muted-foreground">Unsaved changes</span>
            ) : null}
          </div>
        ) : null}

        <ConfirmDialog
          open={confirming}
          onOpenChange={(next) => {
            if (!next) {
              setConfirming(false);
              setError(null);
            }
          }}
          title={`Publish ${planDisplayName(planCode)} v${nextVersion}`}
          description={
            <span>
              Inserts a new immutable config row. The worker reads the new rates
              starting on the next comp event. Earlier versions stay intact.
            </span>
          }
          confirmLabel={`Publish v${nextVersion}`}
          loading={publish.isPending}
          errorMessage={error}
          typedConfirm={{ expected: `PUBLISH v${nextVersion}` }}
          onConfirm={async () => {
            try {
              await publish.mutateAsync();
            } catch {
              // error already surfaced via publish.onError → setError
            }
          }}
        >
          <RateDiffPreview rows={diffRows} />
        </ConfirmDialog>

        {versions.length > 1 ? (
          <details className="pt-3 border-t">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              Version history ({versions.length})
            </summary>
            <ul className="mt-2 space-y-1 text-xs">
              {versions.map((v) => (
                <li key={v.id} className="flex items-center gap-2 text-muted-foreground">
                  <span className="font-mono">v{v.version}</span>
                  <span>·</span>
                  <span>{new Date(v.createdAt).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardContent>
    </Card>
  );
}
